import React from 'react';
import { styled } from '@compiled/react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import type { async_softwareReleasesVersionSummaryDescription_AsyncEditableVersionSummaryDescription$key } from '@atlassian/jira-relay/src/__generated__/async_softwareReleasesVersionSummaryDescription_AsyncEditableVersionSummaryDescription.graphql';
import { lazy } from '@atlassian/react-loosely-lazy';
import { DescriptionContent } from '../../common/ui/DescriptionContent.tsx';
import type { EditableVersionSummaryDescription } from './index.tsx';
import messages from './messages.tsx';

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const LazyEditableSummaryDescription = lazy<typeof EditableVersionSummaryDescription>(() =>
	import(/* webpackChunkName: "async-editable-version-summary-description" */ './index').then(
		(module) => module.EditableVersionSummaryDescription,
	),
);

type Props = {
	shouldTruncateText: boolean;
	jiraVersion: async_softwareReleasesVersionSummaryDescription_AsyncEditableVersionSummaryDescription$key;
};

export const AsyncEditableVersionSummaryDescription = ({
	jiraVersion,
	shouldTruncateText,
}: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const version = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment async_softwareReleasesVersionSummaryDescription_AsyncEditableVersionSummaryDescription on JiraVersion {
				description
				...editableVersionSummaryDescription_softwareReleasesVersionSummaryDescription
			}
		`,
		jiraVersion,
	);

	const descriptionText =
		version.description !== ''
			? version.description
			: formatMessage(messages.descriptionPlaceholder);

	return (
		<Placeholder
			name="editable-description"
			fallback={
				<InlineEditReadViewContainer>
					<DescriptionContent
						descriptionText={descriptionText || ''}
						isNonEmpty={!!version.description}
						shouldTruncateText={shouldTruncateText}
					/>
				</InlineEditReadViewContainer>
			}
		>
			<LazyEditableSummaryDescription
				jiraVersion={version}
				shouldTruncateText={shouldTruncateText}
			/>
		</Placeholder>
	);
};
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InlineEditReadViewContainer = styled.div({
	borderWidth: token('border.width.selected'),
	borderStyle: 'solid',
	borderColor: 'transparent',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: 1,
});
